import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import NotificationToast from './components/common/NotificationToast';

// Pages
import LoginPage from './pages/LoginPage';
import UnifiedLoginPage from './pages/UnifiedLoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailSentPage from './pages/VerifyEmailSentPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import MerchantsListPage from './pages/admin/MerchantsListPage';
import MerchantNewPage from './pages/admin/MerchantNewPage';
import MerchantDetailsPage from './pages/admin/MerchantDetailsPage';
import MerchantEditPage from './pages/admin/MerchantEditPage';
import SubscriptionsPage from './pages/admin/SubscriptionsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import CustomersPage from './pages/CustomersPage';
import LendingHistoryPage from './pages/LendingHistoryPage';
import PaymentPlansPage from './pages/PaymentPlansPage';
import ReferMerchantPage from './pages/ReferMerchantPage';
import SupportPage from './pages/SupportPage';
import AdvertisePage from './pages/AdvertisePage';
import DebtorDetailPage from './pages/DebtorDetailPage';
import SettingsPage from './pages/SettingsPage';
import SubscriptionExpiredPage from './pages/SubscriptionExpiredPage';
import SelectPackagePage from './pages/SelectPackagePage';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import CustomerTransactions from './pages/customer/CustomerTransactions';
import CustomerPaymentSummary from './pages/customer/CustomerPaymentSummary';

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <NotificationToast />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin/login" element={<UnifiedLoginPage />} />
            <Route path="/merchant/login" element={<UnifiedLoginPage />} />
            <Route path="/customer/login" element={<UnifiedLoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email-sent" element={<VerifyEmailSentPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/select-package" element={<SelectPackagePage />} />

            {/* Protected routes (require auth but not necessarily active subscription) */}
            <Route
              path="/subscription-expired"
              element={
                <ProtectedRoute requiresActive={false}>
                  <SubscriptionExpiredPage />
                </ProtectedRoute>
              }
            />

            {/* Protected routes (require active subscription) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={['merchant']}>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['admin']}>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/merchants"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['admin']}>
                  <MerchantsListPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/merchants/new"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['admin']}>
                  <MerchantNewPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/merchants/:merchantId"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['admin']}>
                  <MerchantDetailsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/merchants/:merchantId/edit"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['admin']}>
                  <MerchantEditPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/subscriptions"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['admin']}>
                  <SubscriptionsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['admin']}>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />

            {/* Customer Portal Routes */}
            <Route
              path="/customer/dashboard"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['customer']}>
                  <CustomerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/transactions"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['customer']}>
                  <CustomerTransactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/payment-summary"
              element={
                <ProtectedRoute requiresActive={false} allowedRoles={['customer']}>
                  <CustomerPaymentSummary />
                </ProtectedRoute>
              }
            />

            {/* Merchant Routes */}
            <Route
              path="/customers"
              element={
                <ProtectedRoute allowedRoles={['merchant']}>
                  <CustomersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/lending"
              element={
                <ProtectedRoute allowedRoles={['merchant']}>
                  <LendingHistoryPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/payment-plans"
              element={
                <ProtectedRoute allowedRoles={['merchant']}>
                  <PaymentPlansPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/refer-merchant"
              element={
                <ProtectedRoute allowedRoles={['merchant']}>
                  <ReferMerchantPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/support"
              element={
                <ProtectedRoute allowedRoles={['merchant']}>
                  <SupportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/advertise"
              element={
                <ProtectedRoute allowedRoles={['merchant']}>
                  <AdvertisePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/debtors/:id"
              element={
                <ProtectedRoute allowedRoles={['merchant']}>
                  <DebtorDetailPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={['merchant']}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* 404 */}
            <Route path="*" element={<div className="min-h-screen flex items-center justify-center"><h1 className="text-2xl">404 - Page Not Found</h1></div>} />
          </Routes>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
